/**
 * TariffRecommendationCard - Highlights the recommended tariff and potential savings.
 *
 * Green scheme for >10% savings, yellow/orange for 0-10%.
 * Shows a "switch tariff" call-to-action when current tariff differs from recommended.
 */

import { Card, Text, Badge, Stack, Group, ThemeIcon, Alert } from '@mantine/core';
import { IconStarFilled, IconArrowRight } from '@tabler/icons-react';
import { TariffType } from '../../types';

export interface TariffRecommendationCardProps {
  recommendedTariff: TariffType;
  maxSavingsPln: number;
  maxSavingsPercent: number;
  currentTariff?: TariffType | '';
}

function getSavingsColor(percent: number): string {
  if (percent > 10) return 'green';
  if (percent >= 0) return 'yellow';
  return 'red';
}

function formatPln(amount: number): string {
  return amount.toLocaleString('pl-PL', { maximumFractionDigits: 0 });
}

export function TariffRecommendationCard({
  recommendedTariff,
  maxSavingsPln,
  maxSavingsPercent,
  currentTariff,
}: TariffRecommendationCardProps) {
  const color = getSavingsColor(maxSavingsPercent);
  const showSwitchCTA = currentTariff !== '' && currentTariff != null && currentTariff !== recommendedTariff;

  return (
    <Card withBorder radius="md" p="lg" style={{ borderColor: `var(--mantine-color-${color}-4)` }}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon color={color} variant="light" size="lg" radius="xl">
              <IconStarFilled size={20} />
            </ThemeIcon>
            <Text fw={600} size="lg">Rekomendowana taryfa</Text>
          </Group>
          <Badge color={color} size="lg" variant="light" data-testid="savings-percent-badge">
            +{(maxSavingsPercent ?? 0).toFixed(1)}%
          </Badge>
        </Group>

        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Text fw={700} size="xl" data-testid="recommended-tariff-name">
              {recommendedTariff}
            </Text>
            <Badge color={color} variant="filled" size="md">
              REKOMENDOWANE
            </Badge>
          </Group>
          <Text fw={700} size="lg" c={color} data-testid="max-savings-pln">
            +PLN {formatPln(maxSavingsPln)}/rok
          </Text>
        </Group>

        {showSwitchCTA && (
          <Alert
            icon={<IconArrowRight size={16} />}
            color={color}
            variant="light"
            data-testid="switch-tariff-cta"
          >
            <Text size="sm" fw={500}>
              Przełącz z {currentTariff} na {recommendedTariff}, aby osiągnąć maksymalne oszczędności.
            </Text>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
